/*
 * Shipping protection ("Shipping protection") in the cart drawer.
 *
 * - The protection is a normal Shopify product (tag: shipping-protection) that sits in the cart as its own line.
 * - It is added automatically as soon as the cart has a real product, unless the customer switched it off.
 * - Switching it off removes the line and is remembered; it is also removed when the cart has no real products left.
 * - The drawer body is re-rendered after every change, so everything is driven by the hidden marker
 *   ".rd-protection-sync" that the drawer renders (variant id, real item count, whether the line is in the cart).
 * - The theme's updateCartCounters() counts every cart line, so it is replaced here by a version that skips the protection.
 */
(() => {
  const OPT_OUT_KEY = 'rd-protection-optout';
  const FAILED_KEY = 'rd-protection-failed';
  const PROTECTION_HANDLE = 'protection-dexpedition';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let busy = false;

  const store = {
    get: (area, key) => {
      try {
        return window[area].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set: (area, key, value) => {
      try {
        window[area].setItem(key, value);
      } catch (e) {}
    },
    remove: (area, key) => {
      try {
        window[area].removeItem(key);
      } catch (e) {}
    }
  };

  const isOptedOut = () => store.get('localStorage', OPT_OUT_KEY) === '1';
  const setOptedOut = on => (on ? store.set('localStorage', OPT_OUT_KEY, '1') : store.remove('localStorage', OPT_OUT_KEY));
  const hasFailed = () => store.get('sessionStorage', FAILED_KEY) === '1';
  const setFailed = () => store.set('sessionStorage', FAILED_KEY, '1');

  /* Cart counters (header badge + drawer title) count real products only, not the protection line */
  window.updateCartCounters = async function updateCartCounters() {
    const headerCounters = document.querySelectorAll('.cart-count-badge');
    const drawerCounters = document.querySelectorAll('.cart-drawer__title-counter');

    try {
      const cartUrl = (window.routes && window.routes.cart_url) || root + 'cart';
      const response = await fetch(cartUrl + '.json', { headers: { 'Content-Type': 'application/json' } });
      const state = await response.json();
      const count = (state.items || []).reduce(
        (total, item) => (item.handle === PROTECTION_HANDLE ? total : total + item.quantity),
        0
      );

      headerCounters.forEach(counter => {
        counter.textContent = count;
        counter.classList.toggle('hidden', count === 0);
      });
      drawerCounters.forEach(counter => {
        counter.textContent = count;
      });
    } catch (error) {
      console.error('Cart counters could not be updated:', error);
    }
  };

  const post = (path, body) =>
    fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

  const addProtection = async variantId => {
    const res = await post('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
    return res.ok;
  };

  const removeProtection = async variantId => {
    const res = await post('cart/change.js', { id: variantId, quantity: 0 });
    return res.ok;
  };

  /* Re-render the drawer body with the current cart */
  const refresh = async () => {
    const res = await fetch(root + '?sections=cart-drawer', { headers: { Accept: 'application/json' } });
    const data = await res.json();
    const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = doc.querySelector('#CartDrawer-Body');
    const current = document.querySelector('#CartDrawer-Body');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    window.updateCartCounters();
  };

  const setCounter = count => {
    const counter = document.querySelector('.cart-drawer__title-counter');
    if (counter) counter.textContent = count;
  };

  const sync = async marker => {
    marker.dataset.rdReady = '1';

    const real = Number(marker.dataset.realCount) || 0;
    setCounter(real);

    if (busy || hasFailed() || marker.dataset.available === 'false') return;

    const variantId = Number(marker.dataset.variantId);
    const inCart = marker.dataset.inCart === 'true';
    let action = null;

    if (real === 0) {
      setOptedOut(false);
      if (inCart) action = 'remove';
    } else if (!inCart && !isOptedOut()) {
      action = 'add';
    }

    if (!action) return;

    busy = true;
    try {
      const ok = action === 'add' ? await addProtection(variantId) : await removeProtection(variantId);
      if (!ok) setFailed();
      await refresh();
    } catch (e) {
      setFailed();
    } finally {
      busy = false;
    }
  };

  const scan = () => {
    document.querySelectorAll('.rd-protection-sync:not([data-rd-ready])').forEach(sync);
  };

  /* The customer flips the switch */
  document.addEventListener('change', async event => {
    const input = event.target;
    if (!input || !input.matches || !input.matches('[data-rd-protection-toggle]')) return;

    const marker = document.querySelector('.rd-protection-sync');
    if (!marker || busy) {
      input.checked = !input.checked;
      return;
    }

    const variantId = Number(marker.dataset.variantId);
    busy = true;
    input.disabled = true;

    try {
      if (input.checked) {
        setOptedOut(false);
        await addProtection(variantId);
      } else {
        setOptedOut(true);
        await removeProtection(variantId);
      }
      await refresh();
    } catch (e) {
      input.checked = !input.checked;
    } finally {
      busy = false;
      input.disabled = false;
    }
  });

  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
